/**
 * beradi-stack-drag.js
 * Framer Motion ImgStack-style drag for Beradi image stacks (vanilla JS).
 * Used by: beradi-product-stack (Best Sellers), beradi-drop-stack (All Products).
 */
(function () {
  if (window.BeradiStackDrag) return;

  function isTouchPointer(e) {
    return e.pointerType === 'touch';
  }

  window.BeradiStackDrag = {
    MIN_DRAG: 50,
    COMMIT_DISTANCE: 8,
    ANIM_LOCK_MS: 300,
    DRAG_ELASTIC: 0.2,
    DRAG_CONSTRAINT: 150,

    bind: function (host, cardSelector, options) {
      options = options || {};
      var MIN_DRAG = this.MIN_DRAG;
      var COMMIT_DISTANCE = this.COMMIT_DISTANCE;
      var ANIM_LOCK_MS = this.ANIM_LOCK_MS;
      var DRAG_ELASTIC = this.DRAG_ELASTIC;
      var DRAG_CONSTRAINT = this.DRAG_CONSTRAINT;
      var carouselHandoff = !!options.carouselHandoff;

      function capturePointer(card, pointerId) {
        if (!card) return;
        try {
          card.setPointerCapture(pointerId);
        } catch (err) {}
      }

      function releasePointer(card, pointerId) {
        if (!card) return;
        try {
          card.releasePointerCapture(pointerId);
        } catch (err) {}
      }

      function getCarouselTrack(hostEl) {
        return hostEl.closest('[id^="Slider-"]');
      }

      function lockCarouselScroll(hostEl) {
        if (!carouselHandoff) return;
        var track = getCarouselTrack(hostEl);
        if (!track) return;
        hostEl._lockedScroll = track.scrollLeft;
        track.classList.add('is-stack-active');

        if (!hostEl._scrollLockHandler) {
          hostEl._scrollLockHandler = function () {
            if (hostEl._lockedScroll == null) return;
            if (track.scrollLeft !== hostEl._lockedScroll) {
              track.scrollLeft = hostEl._lockedScroll;
            }
          };
        }
        track.addEventListener('scroll', hostEl._scrollLockHandler, { passive: true });
      }

      function unlockCarouselScroll(hostEl, restore) {
        if (!carouselHandoff) return;
        var track = getCarouselTrack(hostEl);
        if (!track) return;

        if (hostEl._scrollLockHandler) {
          track.removeEventListener('scroll', hostEl._scrollLockHandler);
        }

        var locked = hostEl._lockedScroll;

        function applyLocked() {
          if (restore && locked != null) track.scrollLeft = locked;
        }

        applyLocked();
        requestAnimationFrame(function () {
          applyLocked();
          requestAnimationFrame(function () {
            track.classList.remove('is-stack-active');
            applyLocked();
            track.dispatchEvent(new CustomEvent('beradi-carousel-restored', { bubbles: false }));
            hostEl._lockedScroll = null;
          });
        });
      }

      function applyElasticOffset(value) {
        var limit = DRAG_CONSTRAINT;
        var abs = Math.abs(value);
        if (abs <= limit) return value;
        var sign = value > 0 ? 1 : -1;
        return sign * (limit + (abs - limit) * DRAG_ELASTIC);
      }

      function updateCardPosition(card, dx, dy) {
        card.style.setProperty('--drag-x', applyElasticOffset(dx) + 'px');
        card.style.setProperty('--drag-y', applyElasticOffset(dy) + 'px');
      }

      function clearCardDrag(card) {
        if (!card) return;
        card.style.removeProperty('--drag-x');
        card.style.removeProperty('--drag-y');
      }

      function setAnimating(active) {
        host.isAnimating = active;
        host.classList.toggle('is-animating', active);
      }

      function resetDragState() {
        host.dragging = false;
        host.pendingDrag = false;
        host.activeCard = null;
        host.activePointerId = null;
        host._endingDrag = false;
        host.classList.remove('is-interacting');
      }

      function unbindDocumentPointers() {
        if (!host._docPointersBound) return;
        host._docPointersBound = false;
        document.removeEventListener('pointermove', handlePointerMove, true);
        document.removeEventListener('pointerup', handlePointerEnd, true);
        document.removeEventListener('pointercancel', handlePointerCancel, true);
      }

      function bindDocumentPointers() {
        if (host._docPointersBound) return;
        host._docPointersBound = true;
        document.addEventListener('pointermove', handlePointerMove, { passive: false, capture: true });
        document.addEventListener('pointerup', handlePointerEnd, true);
        document.addEventListener('pointercancel', handlePointerCancel, true);
      }

      function shouldHandoffToCarousel(dx, dy, pointerType) {
        if (!carouselHandoff) return false;
        var ratio = pointerType === 'touch' ? 1.65 : 1.35;
        var minDx = pointerType === 'touch' ? 28 : 18;
        return Math.abs(dx) > Math.abs(dy) * ratio && Math.abs(dx) > minDx;
      }

      function fireCarouselHandoff(e) {
        unlockCarouselScroll(host, false);
        var track = host.closest('[id^="Slider-"]');
        if (track) {
          track.dispatchEvent(
            new CustomEvent('beradi-carousel-handoff', {
              bubbles: false,
              detail: { clientX: e.clientX, pointerId: e.pointerId }
            })
          );
        }
      }

      function handoffToCarousel(e) {
        var card = host.activeCard;
        if (card) {
          card.classList.remove('is-dragging');
          clearCardDrag(card);
          releasePointer(card, e.pointerId);
        }
        unbindDocumentPointers();
        resetDragState();
        fireCarouselHandoff(e);
        return true;
      }

      function commitDrag(card, pointerId) {
        host.pendingDrag = false;
        card.classList.add('is-dragging');
        host.classList.add('is-interacting');
        if (carouselHandoff) lockCarouselScroll(host);
        capturePointer(card, pointerId);
      }

      function handlePointerMove(e) {
        if (!host.dragging || !host.activeCard || host.isAnimating) return;
        if (host.activePointerId != null && e.pointerId !== host.activePointerId) return;

        var dx = e.clientX - host.start.x;
        var dy = e.clientY - host.start.y;
        var dist = Math.sqrt(dx * dx + dy * dy);
        var isCommitted = host.activeCard.classList.contains('is-dragging');

        if (host.pendingDrag) {
          if (dist < COMMIT_DISTANCE) return;

          if (shouldHandoffToCarousel(dx, dy, e.pointerType)) {
            handoffToCarousel(e);
            return;
          }

          commitDrag(host.activeCard, e.pointerId);
          isCommitted = true;
        }

        if (isCommitted && shouldHandoffToCarousel(dx, dy, e.pointerType)) {
          handoffToCarousel(e);
          return;
        }

        if (isCommitted) e.preventDefault();
        if (dist > 4) host.didDrag = true;
        updateCardPosition(host.activeCard, dx, dy);
      }

      function snapBack(card) {
        card.classList.remove('is-dragging');
        clearCardDrag(card);
        if (carouselHandoff) unlockCarouselScroll(host, true);
      }

      function cycleTopCard() {
        var cycled = host.cards.shift();
        host.cards.push(cycled);
        if (typeof host.afterCycle === 'function') {
          host.afterCycle(cycled);
        }
        host.layout();
        return cycled;
      }

      function endDrag(e) {
        if (!host.dragging || host._endingDrag || host.isAnimating) return;
        if (host.activePointerId != null && e.pointerId !== host.activePointerId) return;

        host._endingDrag = true;
        unbindDocumentPointers();

        var card = host.activeCard;
        if (!card) {
          resetDragState();
          if (carouselHandoff) unlockCarouselScroll(host, true);
          return;
        }

        var dx = e.clientX - host.start.x;
        var dy = e.clientY - host.start.y;
        var dist = Math.sqrt(dx * dx + dy * dy);
        var wasCommitted = card.classList.contains('is-dragging');
        var touch = isTouchPointer(e);
        var shouldCycle = wasCommitted && dist >= MIN_DRAG;

        if (!shouldCycle) {
          snapBack(card);
          releasePointer(card, e.pointerId);
          resetDragState();
          return;
        }

        if (touch && carouselHandoff && shouldHandoffToCarousel(dx, dy, e.pointerType)) {
          handoffToCarousel(e);
          return;
        }

        host.didDrag = true;
        setAnimating(true);
        card.classList.remove('is-dragging');
        card.classList.remove('is-top');
        clearCardDrag(card);

        cycleTopCard();

        releasePointer(card, e.pointerId);
        resetDragState();
        if (carouselHandoff) unlockCarouselScroll(host, true);

        window.setTimeout(function () {
          setAnimating(false);
        }, ANIM_LOCK_MS);
      }

      function handlePointerEnd(e) {
        endDrag(e);
      }

      function handlePointerCancel(e) {
        if (!host.dragging || host._endingDrag || !host.activeCard) return;
        if (host.activePointerId != null && e.pointerId !== host.activePointerId) return;

        if (host.activeCard.classList.contains('is-dragging')) {
          capturePointer(host.activeCard, e.pointerId);
          return;
        }

        endDrag(e);
      }

      host.isAnimating = false;

      host.addEventListener(
        'pointerdown',
        function (e) {
          if (host.isAnimating) return;

          var top = host.cards[0];
          if (!top || !top.classList.contains('is-top')) return;
          if (e.button !== undefined && e.button !== 0) return;
          if (e.target.closest(cardSelector) !== top) return;

          var touch = isTouchPointer(e);

          if (touch) e.preventDefault();

          host.dragging = true;
          host.pendingDrag = !touch;
          host.didDrag = false;
          host.activeCard = top;
          host.activePointerId = e.pointerId;
          host._endingDrag = false;
          host.start = { x: e.clientX, y: e.clientY };

          capturePointer(top, e.pointerId);
          bindDocumentPointers();

          if (touch) {
            commitDrag(top, e.pointerId);
            updateCardPosition(top, 0, 0);
          }
        },
        { passive: false }
      );

      host.addEventListener('lostpointercapture', function (e) {
        if (!host.dragging || host._endingDrag || !host.activeCard || host.isAnimating) return;
        if (e.target !== host.activeCard) return;
        if (e.pointerId !== host.activePointerId) return;
        if (!host.activeCard.classList.contains('is-dragging')) return;
        capturePointer(host.activeCard, e.pointerId);
      });
    }
  };
})();
